import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ContainerDetail, StatusFlag } from '@ctp/shared';
import { ApiError, containersApi } from '../lib/api';
import { Badge, Modal, Spinner } from './ui';

/** Eine Definitions-Zeile (Label links, Wert rechts). */
function Row({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <div className="flex justify-between gap-4 py-1 text-sm">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium">{children}</dd>
    </div>
  );
}

/** Ampel-Pille für ein Terminal-Statusfeld (grün/rot/grau). */
function FlagPill({ label, flag }: { label: string; flag: StatusFlag }): JSX.Element {
  const cls =
    flag === 'ok'
      ? 'bg-status-ok/15 text-status-ok'
      : flag === 'problem'
        ? 'bg-status-problem/15 text-status-problem'
        : 'bg-status-unknown/15 text-status-unknown';
  return (
    <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
}

/**
 * Premium-Detailansicht (Klick auf eine Premium-Zeile). Lädt `GET /containers/:id` und zeigt den
 * kompletten Stand: Reise (Schiff/Häfen/ETA-ATA), Milestones, Demurrage/Detention, Terminal-Ampeln,
 * Leerdepot-Stand sowie die vollständige Ereignis-Timeline (alle Milestones). White-Label (D6):
 * keine Quelle/kein Anbieter wird genannt.
 */
export function ContainerDetailModal({
  containerId,
  containerNumber,
  token,
  onClose,
  fmtDate,
}: {
  containerId: string | null;
  containerNumber: string | null;
  token?: string | null;
  onClose: () => void;
  fmtDate: (iso: string | null) => string;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const [detail, setDetail] = useState<ContainerDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fmtDateTime = (iso: string | null | undefined): string => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(i18n.language);
  };

  useEffect(() => {
    if (!containerId) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setDetail(null);
    containersApi
      .detail(containerId, token)
      .then((d) => !cancelled && setDetail(d))
      .catch(
        (err) =>
          !cancelled && setError(err instanceof ApiError ? err.message : t('premium.detail.error')),
      )
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [containerId, token, t]);

  const s = detail?.status;
  // Reise-Abschluss (EPIC Q / ADR-041): Badge im Header + Hinweis, dass keine Aktualisierung mehr läuft.
  const completed = !!(detail?.journeyCompletedAt || detail?.archivedAt);

  return (
    <Modal
      open={!!containerId}
      onClose={onClose}
      size="lg"
      title={
        <span className="flex items-center gap-2">
          <span className="font-mono">{containerNumber ?? '—'}</span>
          {s?.sizeType && <span className="text-xs text-muted-foreground">{s.sizeType}</span>}
          {completed ? (
            <Badge variant="default" title={t('archive.refreshBlockedTooltip')}>
              {t('archive.badge')}
            </Badge>
          ) : (
            detail?.plan === 'premium' && <Badge variant="purple">{t('premium.badge')}</Badge>
          )}
        </span>
      }
    >
      {loading && (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      )}
      {error && <p className="py-4 text-sm text-destructive">{error}</p>}
      {completed && (
        <p className="mb-4 rounded-md border border-border bg-secondary/60 px-3 py-2 text-sm text-muted-foreground">
          {t('archive.refreshBlockedTooltip')}
        </p>
      )}
      {s && (
        <div className="space-y-5">
          {/* Reise */}
          <section>
            <h4 className="mb-1 text-sm font-semibold">{t('premium.detail.journey')}</h4>
            <dl className="divide-y divide-border/60">
              {(s.vessel || s.vesselImo) && (
                <Row label={t('lite.vessel')}>
                  {s.vessel ?? '—'}
                  {s.vesselImo && (
                    <span className="ml-1 text-xs text-muted-foreground">IMO {s.vesselImo}</span>
                  )}
                </Row>
              )}
              {(s.originPort || s.destinationPort) && (
                <Row label={t('premium.detail.route')}>
                  {s.originPort ?? '—'}
                  <span className="text-muted-foreground"> → </span>
                  {s.destinationPort ?? '—'}
                </Row>
              )}
              {s.port && <Row label={t('lite.port')}>{s.port}</Row>}
              {s.terminal && <Row label={t('lite.terminal')}>{s.terminal}</Row>}
              <Row label={t('premium.milestones.eta')}>{fmtDateTime(s.eta)}</Row>
              <Row label={t('premium.milestones.etd')}>{fmtDateTime(s.etd)}</Row>
              {s.ata && <Row label={t('premium.detail.ata')}>{fmtDateTime(s.ata)}</Row>}
              {s.atd && <Row label={t('premium.detail.atd')}>{fmtDateTime(s.atd)}</Row>}
            </dl>
          </section>

          {/* Milestones + D&D */}
          <section>
            <h4 className="mb-1 text-sm font-semibold">{t('premium.detail.milestones')}</h4>
            <dl className="divide-y divide-border/60">
              <Row label={t('premium.milestones.discharged')}>{fmtDateTime(s.dischargedAt)}</Row>
              <Row label={t('premium.milestones.gateOut')}>{fmtDateTime(s.gateOutAt)}</Row>
              <Row label={t('premium.milestones.emptyReturned')}>
                {fmtDateTime(s.emptyReturnedAt)}
              </Row>
              <Row label={t('premium.dnd.demurrage')}>
                {detail.demurrageDaysLeft == null
                  ? '—'
                  : t('premium.dnd.remaining', { days: detail.demurrageDaysLeft })}
              </Row>
              <Row label={t('premium.dnd.detention')}>
                {detail.detentionDaysLeft == null
                  ? '—'
                  : t('premium.dnd.remaining', { days: detail.detentionDaysLeft })}
              </Row>
            </dl>
          </section>

          {/* Terminal-Status */}
          <section>
            <h4 className="mb-2 text-sm font-semibold">{t('premium.detail.terminalStatus')}</h4>
            <div className="flex flex-wrap gap-2">
              <FlagPill
                label={`${t('lite.fields.discharge')}: ${t('status.' + s.discharge)}`}
                flag={s.discharge}
              />
              <FlagPill
                label={`${t('lite.fields.release')}: ${t('status.' + s.release)}`}
                flag={s.release}
              />
              <FlagPill
                label={`${t('lite.fields.customs')}: ${t('status.' + s.customs)}`}
                flag={s.customs}
              />
              <FlagPill
                label={`${t('lite.fields.block')}: ${t('status.' + s.block)}`}
                flag={s.block}
              />
              <FlagPill
                label={`${t('lite.fields.pickup')}: ${t('status.' + s.pickup)}`}
                flag={s.pickup}
              />
            </div>
            {detail.emptyDepotName && (
              <p className="mt-2 text-sm">
                <span className="text-muted-foreground">{t('dashboard.col.depot')}: </span>
                {detail.emptyDepotName}
                {detail.emptyDepotStatus && (
                  <span className="ml-1 text-muted-foreground">({detail.emptyDepotStatus})</span>
                )}
              </p>
            )}
          </section>

          {/* Timeline (alle Milestones) */}
          {s.timeline && s.timeline.length > 0 && (
            <section className="border-t pt-4">
              <h4 className="mb-2 text-sm font-semibold">{t('timeline.heading')}</h4>
              <ul className="space-y-1.5">
                {s.timeline.map((ev, i) => (
                  <li
                    key={`${ev.event_key}-${i}`}
                    className="flex flex-wrap items-center gap-2 text-sm"
                  >
                    <span className="w-28 shrink-0 text-muted-foreground">
                      {fmtDate(ev.occurred_at)}
                    </span>
                    <span className="min-w-[12rem] flex-1">
                      {t(`timeline.events.${ev.event_key}`, { defaultValue: ev.event_key })}
                      {ev.location && (
                        <span className="ml-1 font-medium text-foreground">· {ev.location}</span>
                      )}
                    </span>
                    {ev.is_estimated && (
                      <Badge className="bg-foreground/80 text-background">
                        {t('timeline.estimated')}
                      </Badge>
                    )}
                    {ev.sources.map((src, j) => (
                      <Badge key={`${src.badge}-${j}`} variant="default">
                        {t(`timeline.source.${src.badge}`, { defaultValue: src.badge })}
                      </Badge>
                    ))}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </Modal>
  );
}
